import type { SupportedMethod } from './types';

export const normalizeSupportedMethodId = (m: SupportedMethod): string => {
  return String(m.methodId || m.id || '').trim();
};

export const formatProviderDisplayName = (providerRaw: string): string => {
  const provider = (providerRaw || '').trim().toLowerCase();
  if (provider === 'online_debt') return 'Internal balance';
  if (provider === 'zeroxprocessing') return '0xProcessing';
  if (provider === 'linkmepay') return 'LinkMePay';
  if (provider === 'btcpay') return 'BTCPay';
  if (provider === 'meld') return 'Meld';
  return providerRaw || provider;
};

export const formatOnlineDebtMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  if (methodId === 'internal' || methodId === 'all') return 'Internal balance';
  return 'Internal balance';
};

const BTCPAY_WITHDRAWAL_METHOD_LABELS: Record<string, { name: string; ticker: string }> = {
  bitcoin_transfer: { name: 'Bitcoin On-Chain', ticker: 'BTC' },
  litecoin_transfer: { name: 'Litecoin', ticker: 'LTC' },
  dogecoin_transfer: { name: 'Dogecoin', ticker: 'DOGE' },
};

export const formatBtcpayWithdrawalMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  const mapped = BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId];
  if (mapped) return mapped.name;
  return methodIdRaw || 'Method';
};

const HARD_CODED_SUPPORTED_METHODS_BY_KEY: Record<string, SupportedMethod[]> = {
  'linkmepay::deposit': [
    { methodId: 'ecash_app', name: 'ECash App', ticker: 'ECASH' },
    { methodId: 'paypal', name: 'PayPal', ticker: 'PAYPAL' },
  ],
  'btcpay::deposit': [
    { methodId: 'btc_onchain', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'ltc_chain', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'doge_chain', name: 'Dogecoin', ticker: 'DOGE' },
  ],
  'btcpay::withdrawal': [
    { methodId: 'bitcoin_transfer', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'litecoin_transfer', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'dogecoin_transfer', name: 'Dogecoin', ticker: 'DOGE' },
  ],
};

export const mergeWithHardcodedSupportedMethods = (
  providerRaw: string,
  methods: SupportedMethod[],
  operationRaw = ''
): SupportedMethod[] => {
  const provider = (providerRaw || '').trim().toLowerCase();
  const operation = String(operationRaw || '').trim().toLowerCase();
  const hardcoded =
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::${operation}`] ||
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::`] ||
    [];

  const out: SupportedMethod[] = [];
  const seen = new Set<string>();

  const push = (m: SupportedMethod) => {
    const id = normalizeSupportedMethodId(m).trim().toLowerCase();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(m);
  };

  (Array.isArray(methods) ? methods : []).forEach(push);
  hardcoded.forEach(push);

  return out;
};

export const formatZeroxMethodIdLabel = (methodIdRaw: string): string => {
  const id = String(methodIdRaw || '').trim().toLowerCase();
  if (!id.startsWith('zerox_')) return methodIdRaw || 'Method';
  const rest = id.replace(/^zerox_/, '');
  if (rest === 'eth') return 'ETH';
  if (rest === 'usdt') return 'USDT';
  if (rest.startsWith('eth_')) {
    const network = rest
      .slice(4)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `ETH (${network})` : 'ETH';
  }
  if (rest.startsWith('usdt_')) {
    const network = rest
      .slice(5)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `USDT (${network})` : 'USDT';
  }
  return rest.toUpperCase();
};

export const prettyMethodLabel = (m: SupportedMethod): string => {
  const methodId = normalizeSupportedMethodId(m);
  const displayName = String(m.name || m.currency || '').trim();
  const ticker = String(m.ticker || '').trim();

  if (displayName.includes('(')) return displayName;
  if (displayName && ticker && displayName.length > ticker.length) return displayName;

  const t = (ticker || displayName).toUpperCase();
  const inferredNetwork =
    t === 'BTC'
      ? 'Bitcoin Network'
      : t === 'LTC'
        ? 'Litecoin Network'
        : t === 'DOGE'
          ? 'Dogecoin Network'
          : t
            ? `${t} Network`
            : '';
  if (ticker && inferredNetwork) return `${ticker} — ${inferredNetwork}`;
  if (displayName) return displayName;
  if (ticker) return ticker;
  if (methodId.startsWith('zerox_')) return formatZeroxMethodIdLabel(methodId);
  if (BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId]) return formatBtcpayWithdrawalMethodLabel(methodId);
  return methodId || 'Method';
};

export const splitCoinAndNetworkLabel = (
  methodLabel: string,
  methodId = ''
): { coin: string; network: string } => {
  const label = String(methodLabel || '').trim();
  const id = String(methodId || '').trim().toLowerCase();

  const parenMatch = label.match(/^([^(]+)\(([^)]+)\)\s*$/);
  if (parenMatch) {
    return { coin: parenMatch[1].trim(), network: parenMatch[2].trim() };
  }

  const split = label.split(/\s*[-—–/|]\s*|\s{2,}/);
  if (split.length >= 2 && split[1]?.trim()) {
    return { coin: split[0].trim(), network: split[1].trim() };
  }

  if (id.startsWith('zerox_eth_')) {
    return {
      coin: 'ETH',
      network: id
        .replace('zerox_eth_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id.startsWith('zerox_usdt_')) {
    return {
      coin: 'USDT',
      network: id
        .replace('zerox_usdt_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id === 'zerox_eth') return { coin: 'ETH', network: 'Default' };
  if (id === 'zerox_usdt') return { coin: 'USDT', network: 'Default' };

  return { coin: label || '—', network: '' };
};

export const toPercentString = (feeRate: number): string => {
  const n = Number(feeRate);
  if (!Number.isFinite(n) || n < 0) return '0';
  const pct = n * 100;
  const rounded = Math.round(pct * 100 + Number.EPSILON) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
};

export const toRateNumber = (feePercent: string): number => {
  const p = Number(String(feePercent || '').trim());
  if (!Number.isFinite(p) || p < 0) return 0;
  return p / 100;
};

export const toMoneyString = (amount: number): string => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const normalizeNetworkFeeType = (raw: unknown): import('./types').NetworkFeeType => {
  const t = String(raw || 'none').trim().toLowerCase();
  if (t === 'percent' || t === 'fixed') return t;
  return 'none';
};

export const formatNetworkFeeDisplay = (
  type: import('./types').NetworkFeeType,
  percent: string,
  fixedUsd: string
): string => {
  if (type === 'percent') return `${percent || '0'}%`;
  if (type === 'fixed') return `$${fixedUsd || '0'}`;
  return '—';
};

export const networkFeeFieldsFromItem = (it: {
  networkFeeType?: string;
  networkFeeRate?: number;
  networkFeeAmount?: number;
}) => {
  const networkFeeType = normalizeNetworkFeeType(it.networkFeeType);
  return {
    networkFeeType,
    networkFeePercent: networkFeeType === 'percent' ? toPercentString(it.networkFeeRate ?? 0) : '0',
    networkFeeFixedUsd:
      networkFeeType === 'fixed' ? toMoneyString(Number(it.networkFeeAmount ?? 0)) : '0',
  };
};

export const toSupportedKey = (provider: string, operation: string) =>
  `${(provider || '').trim().toLowerCase()}::${String(operation || '').trim().toLowerCase()}`;

export function providerFeeRowId(row: { provider: string; operation: string; methodId: string }): string {
  return `${(row.provider || '').toLowerCase()}::${row.operation}::${(row.methodId || 'all').toLowerCase()}`;
}

export function getCoinVisual(methodId: string, methodLabel: string): {
  symbol: string;
  colorClass: string;
  textClass: string;
} {
  const id = methodId.toLowerCase();
  const label = methodLabel.toLowerCase();
  const isBtc = id.includes('btc') || id.includes('bitcoin') || label.includes('bitcoin');
  const isLtc = id.includes('ltc') || id.includes('litecoin') || label.includes('litecoin');
  const isDoge = id.includes('doge') || id.includes('dogecoin') || label.includes('dogecoin');
  const isEth = id.includes('eth') || id.includes('erc') || label.includes('ethereum');
  const isUsdt = id.includes('usdt') || id.includes('trc') || label.includes('tether');
  const isPaypal = id.includes('paypal') || label.includes('paypal');
  const isCashApp = id.includes('cash_app') || id.includes('ecash') || label.includes('cash app');
  const isCard = id.includes('card') || id.includes('credit') || id.includes('meld') || label.includes('card');
  if (isBtc) return { symbol: '₿', colorClass: 'bg-amber-500/20', textClass: 'text-amber-700' };
  if (isLtc) return { symbol: 'Ł', colorClass: 'bg-slate-500/20', textClass: 'text-slate-700' };
  if (isDoge) return { symbol: 'Ð', colorClass: 'bg-yellow-500/20', textClass: 'text-yellow-700' };
  if (isEth) return { symbol: 'Ξ', colorClass: 'bg-indigo-500/20', textClass: 'text-indigo-700' };
  if (isUsdt) return { symbol: 'T', colorClass: 'bg-emerald-500/20', textClass: 'text-emerald-700' };
  if (isPaypal) return { symbol: 'P', colorClass: 'bg-blue-500/20', textClass: 'text-blue-700' };
  if (isCashApp) return { symbol: '$', colorClass: 'bg-green-500/20', textClass: 'text-green-700' };
  if (isCard) return { symbol: 'C', colorClass: 'bg-purple-500/20', textClass: 'text-purple-700' };
  return { symbol: '•', colorClass: 'bg-stone-500/20', textClass: 'text-stone-600' };
}
